import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Heart } from "lucide-react";

const BadaGanpatiTemple = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/badaganpati0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Bada Ganpati Temple",
    "description": "Historic Ganesh temple in Indore featuring one of the largest Ganesha idols, known for grand celebrations",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bada Ganpati Road",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 05:00-22:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "12000"
    }
  };

  const highlights = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Largest Ganesha Idol",
      description: "Features one of the largest Ganesha idols in Indore, attracting devotees from across the region"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Historical Significance",
      description: "Ancient temple with rich history dating back several centuries, important cultural landmark"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Grand Celebrations",
      description: "Famous for spectacular Ganesh Chaturthi celebrations with elaborate decorations and festivities"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Traditional Architecture",
      description: "Beautiful traditional temple architecture with intricate carvings and spiritual ambiance"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "5:00 AM - 10:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 6 AM, Evening 7 PM" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "45 minutes - 1 hour" },
    { label: "Facilities", value: "Parking, Prasad shop, Wheelchair access" }
  ];

  const festivals = [
    { name: "Ganesh Chaturthi", time: "August/September", special: "Grand 11-day celebrations with spectacular decorations" },
    { name: "Maghi Ganesh Jayanti", time: "January/February", special: "Special winter celebrations and prayers" },
    { name: "Sankashti Chaturthi", time: "Monthly", special: "Monthly fast and special evening prayers" },
    { name: "Angarki Chaturthi", time: "Tuesday Chaturthi", special: "Especially auspicious Chaturthi celebrations" }
  ];

  const nearbyAttractions = [
    { name: "Rajwada Palace", distance: "1.5 km", time: "6 mins" },
    { name: "Kanch Mandir", distance: "2 km", time: "8 mins" },
    { name: "Sarafa Bazaar", distance: "1 km", time: "4 mins" },
    { name: "Central Museum", distance: "2.5 km", time: "10 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Bada Ganpati Temple Indore - Largest Ganesha Idol | Aaoghumen</title>
        <meta name="description" content="Visit Bada Ganpati Temple in Indore with Aaoghumen - historic temple featuring one of the largest Ganesha idols. Complete guide with timings, festival celebrations, and spiritual significance." />
        <meta name="keywords" content="bada ganpati temple indore, bada ganpati timings, largest ganesha idol indore, indore ganesh temple, aaoghumen bada ganpati, ganesh chaturthi indore, bada ganpati history, indore temples" />
        
        <meta property="og:title" content="Bada Ganpati Temple Indore - Magnificent Ganesha | Aaoghumen" />
        <meta property="og:description" content="Experience divine blessings at Bada Ganpati Temple, home to one of the largest Ganesha idols in Indore with grand celebrations and rich history." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/bada-ganpati-temple" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/bada-ganpati-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-orange-600 via-red-500 to-yellow-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Bada Ganpati Temple Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Bada Ganpati Temple</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Experience divine grandeur at Bada Ganpati Temple, home to one of the largest Ganesha idols in Indore, renowned for spectacular celebrations and spiritual significance.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.7 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>5 AM - 10 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Bada Ganpati Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Bada Ganpati Temple stands as one of the most magnificent and historically significant temples in Indore, famous for housing one of the largest Ganesha idols in the city. The temple's name "Bada Ganpati" literally means "Big Ganesha," reflecting the impressive scale of the deity that draws devotees from across Madhya Pradesh and beyond. This sacred temple has been a cornerstone of Indore's spiritual landscape for centuries, playing a vital role in the city's religious and cultural identity.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple is renowned for its spectacular Ganesh Chaturthi celebrations, which transform the entire area into a vibrant festival ground with elaborate decorations, traditional music, and thousands of devotees participating in the festivities. The grand scale of celebrations here is considered one of the finest in central India, attracting visitors who come to witness the magnificent processions and cultural programs that span several days.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, explore the rich history and spiritual significance of this remarkable temple, participate in traditional rituals, and experience the divine energy that has made Bada Ganpati Temple a beloved destination for spiritual seekers and cultural enthusiasts. The temple's commitment to preserving ancient traditions while embracing modern facilities makes it a perfect blend of heritage and accessibility.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-orange-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Grand Celebrations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-orange-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Special Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Experiences</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Magnificent Idol</h3>
                <p className="text-gray-600 mb-4">Marvel at one of the largest Ganesha idols in Indore, beautifully decorated and maintained with traditional care.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Impressive scale and artistry</li>
                  <li>• Daily decoration rituals</li>
                  <li>• Photography opportunities</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Cultural Programs</h3>
                <p className="text-gray-600 mb-4">Enjoy regular cultural programs, devotional music, and traditional performances during festivals.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Traditional music sessions</li>
                  <li>• Religious discourses</li>
                  <li>• Community participation</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Spiritual Offerings</h3>
                <p className="text-gray-600 mb-4">Participate in traditional offerings, prayers, and seek blessings for prosperity and obstacle removal.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Traditional prasad</li>
                  <li>• Special prayer services</li>
                  <li>• Spiritual guidance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Bada Ganpati area, central location</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts of city, ₹25-70</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola readily available, central location</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Street parking available, busy area</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Central Indore, well-connected roads</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Bada Ganpati Temple" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Divine Grandeur</h2>
              <p className="text-xl mb-6">Visit Bada Ganpati Temple with expert guidance and cultural insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Bada Ganpati Road, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Visit
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default BadaGanpatiTemple;